export const SPELLING_KEY = "spelling-and-grammar";

export const ANNOTATION_PALETTE = [
  { main: "#2F9F9A", light: "#DDF4F2" },
  { main: "#572C88", light: "#EEE7F7" },
  { main: "#D99400", light: "#FFF1C7" },
  { main: "#1C874D", light: "#E0F5E8" },
  { main: "#4789FA", light: "#E5F1FF" },
];

const validLocations = (locations, textLength) =>
  (locations || []).filter(
    ({ startIndex, endIndex }) =>
      Number.isInteger(startIndex) &&
      Number.isInteger(endIndex) &&
      startIndex >= 0 &&
      endIndex > startIndex &&
      endIndex <= textLength,
  );

export const normaliseAnnotations = (items, textLength, type = "assessment") =>
  (items || [])
    .map((item, originalIndex) => ({
      ...item,
      originalIndex,
      type,
      locations: validLocations(item.locations, textLength),
    }))
    .sort((left, right) => {
      const leftStart =
        left.locations[0]?.startIndex ?? Number.MAX_SAFE_INTEGER;
      const rightStart =
        right.locations[0]?.startIndex ?? Number.MAX_SAFE_INTEGER;
      return leftStart - rightStart || left.originalIndex - right.originalIndex;
    })
    .map((item, index) => {
      const numberedItem = { ...item, number: index + 1 };
      delete numberedItem.originalIndex;
      return numberedItem;
    });

export const buildAnnotationSegments = (text, annotations) => {
  const boundaries = new Set([0, text.length]);
  annotations.forEach((annotation) =>
    annotation.locations.forEach(({ startIndex, endIndex }) => {
      boundaries.add(startIndex);
      boundaries.add(endIndex);
    }),
  );
  const points = [...boundaries].sort((a, b) => a - b);
  return points.slice(0, -1).map((start, index) => {
    const end = points[index + 1];
    return {
      start,
      end,
      text: text.slice(start, end),
      annotations: annotations.filter((annotation) =>
        annotation.locations.some(
          (location) => location.startIndex < end && location.endIndex > start,
        ),
      ),
    };
  });
};

const offsetForNode = (container, targetNode, targetOffset) => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let total = 0;
  let node = walker.nextNode();
  while (node) {
    if (node.parentElement?.closest("[data-answer-segment='true']")) {
      if (node === targetNode) return total + targetOffset;
      total += node.textContent?.length || 0;
    }
    node = walker.nextNode();
  }
  return null;
};

export const getSelectionOffsets = (container, selection, answerText) => {
  if (!container || !selection?.rangeCount || selection.isCollapsed)
    return null;
  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  const anchor = offsetForNode(
    container,
    range.startContainer,
    range.startOffset,
  );
  const focus = offsetForNode(container, range.endContainer, range.endOffset);
  if (anchor === null || focus === null) return null;
  const startIndex = Math.min(anchor, focus);
  const endIndex = Math.max(anchor, focus);
  if (endIndex <= startIndex) return null;
  return {
    startIndex,
    endIndex,
    text: answerText.slice(startIndex, endIndex),
  };
};

export const countWords = (text) => (text.trim().match(/\S+/g) || []).length;
